import * as React from "react";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { ToastContainer } from "react-toastify";

/*
|---------------------------------------------------------------------
|                             Main
|---------------------------------------------------------------------
*/
import AuthLayout from "./layouts/AuthLayout";
import HomeLayout from "./layouts/HomeLayout";
import Dashboard from "./pages/dashboard/Dashboard";
import Welcome from "./pages/welcome/Welcome";
import Signin from "./pages/auth/Signin";
import Signup from "./pages/auth/Signup";
import MainLayout from "./layouts/MainLayout";
import AllVisits from "./pages/visists/AllVisits";
import Summaries from "./pages/summary/Summaries";
import Analytics from "./pages/analytics/Analytics";
import AllFinances from "./pages/finances/AllFinances";
import Customers from "./pages/customers/AllCustomers";
import Workshop from "./pages/statistics/Workshop";
import Revenues from "./pages/statistics/Revenues";
import AllRankings from "./pages/rankings/AllRankings";
import AllModules from "./pages/admin/AllModules";
import AllHistory from "./pages/services/AllHistory";
import ChartHistory from "./pages/services/ChartHistory";

/*
|---------------------------------------------------------------------
|                             Manage
|---------------------------------------------------------------------
*/
import AllCustomers from "./pages/manage/customers/AllCustomers";
import AllBookings from "./pages/manage/bookings/AllBookings";
import AllCalendar from "./pages/manage/bookings/AllCalendar";
import AllQuotations from "./pages/manage/quotations/AllQuotations";
import AllDrives from "./pages/manage/drives/AllDrives";

import AllBudgets from "./pages/manage/finances/AllBudgets";
import AllUsers from "./pages/manage/users/AllUsers";
import AllRoles from "./pages/manage/roles/AllRoles";
import AllFirms from "./pages/manage/firms/AllFirms";
import AllStations from "./pages/manage/stations/AllStations";
import AllModels from "./pages/manage/models/AllModels";
import AllVehicles from "./pages/manage/vehicles/AllVehicles";
import AllSites from "./pages/manage/sites/AllSites";
import AllQRCodes from "./pages/manage/qrcodes/AllQRCodes";

import Statement from "./pages/documents/statement/Statement";
import Quotation from "./pages/documents/quotation/Quotation";

/*
|---------------------------------------------------------------------
|                             Others
|---------------------------------------------------------------------
*/

import NewVehicles from "./pages/manage/vehicles/NewVehicles";
import NewCustomers from "./pages/manage/customers/NewCustomers";
import NewQRCodes from "./pages/manage/qrcodes/NewQRCodes";

import "antd/dist/antd.min.css";
import "animate.css";
import "react-toastify/dist/ReactToastify.css";
import "./custom.css";
import "./App.css";

import AllAssists from "./pages/manage/assists/AllAssists";

import AllRequests from "./pages/manage/requests/AllRequests";

import { AuthContext, AuthProvider } from "./contexts/AuthContext";
import MyProfile from "./pages/profile/MyProfile";
import Reset from "./pages/auth/Reset";
import Forgot from "./pages/auth/Forgot";
import NewBookings from "./pages/manage/bookings/NewBookings";
import NewCalendar from "./pages/manage/bookings/NewCalendar";
import NewQuotations from "./pages/manage/quotations/NewQuotations";
import NewDrives from "./pages/manage/drives/NewDrives";
import Parts from "./pages/statistics/Parts";
import AllAssets from "./pages/assets/AllAssets";
import WallBookings from "./pages/manage/bookings/WallBookings";
import WallPage from "./pages/manage/bookings/WallPage";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import WallPager from "./pages/manage/bookings/WallPager";
import WallNew from "./pages/manage/bookings/WallNew";

export default function App() {
  const queryClient = new QueryClient();
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Routes>
          <Route element={<HomeLayout />}>
            <Route path="/" element={<Welcome />} />
          </Route>
          <Route element={<AuthLayout />}>
            <Route path="/login" element={<Signin />} />
            <Route path="/register" element={<Signup />} />
            <Route path="/forgot" element={<Forgot />} />
            <Route path="/reset" element={<Reset />} />
            <Route path="/wall/bookings" element={<WallBookings />} />
            <Route path="/wall/one" element={<WallPage />} />
            <Route path="/wall/old" element={<WallPager />} />
          </Route>
          <Route path="/wall" element={<WallNew />} />
          <Route element={<MainLayout />}>
            <Route
              path="/dashboard"
              element={
                <RequireAuth>
                  <Dashboard />
                </RequireAuth>
              }
            />
            <Route
              path="/revenues"
              element={
                <RequireAuth>
                  <Revenues />
                </RequireAuth>
              }
            />
            <Route
              path="/revenues/workshop"
              element={
                <RequireAuth>
                  <Workshop />
                </RequireAuth>
              }
            />
            <Route
              path="/revenues/parts"
              element={
                <RequireAuth>
                  <Parts />
                </RequireAuth>
              }
            />
            <Route
              path="/visits"
              element={
                <RequireAuth>
                  <AllVisits />
                </RequireAuth>
              }
            />
            <Route
              path="/summary"
              element={
                <RequireAuth>
                  <Summaries />
                </RequireAuth>
              }
            />

            <Route
              path="/finances"
              element={
                <RequireAuth>
                  <AllFinances />
                </RequireAuth>
              }
            />
            <Route
              path="/assets"
              element={
                <RequireAuth>
                  <AllAssets />
                </RequireAuth>
              }
            />
            <Route
              path="/analytics"
              element={
                <RequireAuth>
                  <Analytics />
                </RequireAuth>
              }
            />
            <Route
              path="/customers"
              element={
                <RequireAuth>
                  <Customers />
                </RequireAuth>
              }
            />
            <Route
              path="/local/customers"
              element={
                <RequireAuth>
                  <NewCustomers />
                </RequireAuth>
              }
            />
            <Route
              path="/local/vehicles"
              element={
                <RequireAuth>
                  <NewVehicles />
                </RequireAuth>
              }
            />
            <Route
              path="/local/qrcodes"
              element={
                <RequireAuth>
                  <NewQRCodes />
                </RequireAuth>
              }
            />
            <Route
              path="/service/history"
              element={
                <RequireAuth>
                  <AllHistory />
                </RequireAuth>
              }
            />
            <Route
              path="/service/history/charts"
              element={
                <RequireAuth>
                  <ChartHistory />
                </RequireAuth>
              }
            />
            <Route
              path="/rankings"
              element={
                <RequireAuth>
                  <AllRankings />
                </RequireAuth>
              }
            />
            <Route
              path="/admin"
              element={
                <RequireAuth>
                  <AllModules />
                </RequireAuth>
              }
            />

            <Route
              path="/profile"
              element={
                <RequireAuth>
                  <MyProfile />
                </RequireAuth>
              }
            />
            <Route
              path="/manage/customers"
              element={
                <RequireAuth>
                  <AllCustomers />
                </RequireAuth>
              }
            />
            <Route
              path="/manage/bookings"
              element={
                <RequireAuth>
                  <AllBookings />
                </RequireAuth>
              }
            />
            <Route
              path="/manage/bookings/calendar"
              element={
                <RequireAuth>
                  <AllCalendar />
                </RequireAuth>
              }
            />
            <Route
              path="/manage/quotations"
              element={
                <RequireAuth>
                  <AllQuotations />
                </RequireAuth>
              }
            />
            <Route
              path="/manage/assists"
              element={
                <RequireAuth>
                  <AllAssists />
                </RequireAuth>
              }
            />
            <Route
              path="/manage/testdrives"
              element={
                <RequireAuth>
                  <AllDrives />
                </RequireAuth>
              }
            />
            <Route
              path="/manage/requests"
              element={
                <RequireAuth>
                  <AllRequests />
                </RequireAuth>
              }
            />
            <Route
              path="/manage/finances"
              element={
                <RequireAuth>
                  <AllBudgets />
                </RequireAuth>
              }
            />
            <Route
              path="/manage/users"
              element={
                <RequireAuth>
                  <AllUsers />
                </RequireAuth>
              }
            />
            <Route
              path="/manage/roles"
              element={
                <RequireAuth>
                  <AllRoles />
                </RequireAuth>
              }
            />
            <Route
              path="/manage/firms"
              element={
                <RequireAuth>
                  <AllFirms />
                </RequireAuth>
              }
            />
            <Route
              path="/manage/models"
              element={
                <RequireAuth>
                  <AllModels />
                </RequireAuth>
              }
            />
            <Route
              path="/manage/vehicles"
              element={
                <RequireAuth>
                  <AllVehicles />
                </RequireAuth>
              }
            />
            <Route
              path="/manage/sites"
              element={
                <RequireAuth>
                  <AllSites />
                </RequireAuth>
              }
            />
            <Route
              path="/manage/stations"
              element={
                <RequireAuth>
                  <AllStations />
                </RequireAuth>
              }
            />
            <Route
              path="/manage/qrcodes"
              element={
                <RequireAuth>
                  <AllQRCodes />
                </RequireAuth>
              }
            />

            {/* Others */}

            <Route
              path="/bookings"
              element={
                <RequireAuth>
                  <NewBookings />
                </RequireAuth>
              }
            />

            <Route
              path="/bookings/calendar"
              element={
                <RequireAuth>
                  <NewCalendar />
                </RequireAuth>
              }
            />
            <Route
              path="/quotations"
              element={
                <RequireAuth>
                  <NewQuotations />
                </RequireAuth>
              }
            />

            <Route
              path="/testdrives"
              element={
                <RequireAuth>
                  <NewDrives />
                </RequireAuth>
              }
            />
          </Route>
          <Route element={<AuthLayout />}>
            <Route path="/statement/:account" element={<Statement />} />
            <Route path="/quotation/:id" element={<Quotation />} />
          </Route>
        </Routes>
        <ToastContainer
          position="top-left"
          autoClose={5000}
          hideProgressBar={true}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
        />
      </AuthProvider>
    </QueryClientProvider>
  );
}

function RequireAuth({ children }: { children: JSX.Element }) {
  let auth = React.useContext(AuthContext);
  let location = useLocation();

  // if (!auth.token) {
  //   return <Navigate to="/login" state={{ from: location }} replace />;
  // }

  return children;
}
